import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate } from "react-router-dom";
import { AppLayout } from "../components/layout";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2 } from "lucide-react";
import {
  getAllFeedback,
  getAllFeedbackStats,
} from "../services/api";
import { useHelpCenterStore } from "@/store/helpCenterStore";
import toast from "react-hot-toast";
import { useIsProCloudPlan } from "../store/userStore";
import { usePenpal } from "../hooks/usePenpal";
import {
  ReportsStatsCards,
  ReportsFilters,
  ReportsTable,
  FeedbackDetailsDrawer,
  PaidFeatureBanner,
  formatDate,
  getRatingIcon,
  getItemUrl,
} from "../components/reports";

interface FilterState {
  feedbackType: string;
  rating: number | undefined | null;
  userHash: string;
  startDate: string;
  endDate: string;
  page: number;
  limit: number;
}

const ReportsPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const isProCloudPlan = useIsProCloudPlan();
  const { helpCenters, fetchHelpCenters } = useHelpCenterStore();

  const [feedback, setFeedback] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Initialize filters from URL params
  const getFiltersFromParams = useCallback((): FilterState => {
    const ratingParam = searchParams.get("rating");
    let rating: number | undefined | null = undefined;
    if (ratingParam === "1") rating = 1;
    else if (ratingParam === "0") rating = 0;
    else if (ratingParam === "null") rating = null;

    const pageParam = searchParams.get("page");
    const page = pageParam ? parseInt(pageParam, 10) : 1;

    return {
      feedbackType: searchParams.get("type") || "",
      rating,
      userHash: searchParams.get("userHash") || "",
      startDate: searchParams.get("startDate") || "",
      endDate: searchParams.get("endDate") || "",
      page: isNaN(page) ? 1 : page,
      limit: 50,
    };
  }, [searchParams]);

  const [filters, setFilters] = useState<FilterState>(() => {
    const ratingParam = searchParams.get("rating");
    let rating: number | undefined | null = undefined;
    if (ratingParam === "1") rating = 1;
    else if (ratingParam === "0") rating = 0;
    else if (ratingParam === "null") rating = null;

    const pageParam = searchParams.get("page");
    const page = pageParam ? parseInt(pageParam, 10) : 1;

    return {
      feedbackType: searchParams.get("type") || "",
      rating,
      userHash: searchParams.get("userHash") || "",
      startDate: searchParams.get("startDate") || "",
      endDate: searchParams.get("endDate") || "",
      page: isNaN(page) ? 1 : page,
      limit: 50,
    };
  });
  const selectedHelpCenterId = searchParams.get("helpCenterId") || "";

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
  });

  const [selectedFeedback, setSelectedFeedback] = useState<any | null>(null);
  const [showFeedbackDrawer, setShowFeedbackDrawer] = useState(false);
  const { openExternalUrl } = usePenpal();

  // Sync filters from URL params when they change (browser back/forward)
  useEffect(() => {
    const newFilters = getFiltersFromParams();
    setFilters(newFilters);
  }, [getFiltersFromParams]);

  useEffect(() => {
    fetchHelpCenters();
  }, [fetchHelpCenters]);

  useEffect(() => {
    fetchFeedback();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedHelpCenterId, filters]);

  const fetchFeedback = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getAllFeedback({
        helpCenterId: selectedHelpCenterId || undefined,
        feedbackType: filters.feedbackType || undefined,
        rating: filters.rating,
        userHash: filters.userHash || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        page: filters.page,
        limit: filters.limit,
      });

      if (response.ok && response.feedback) {
        setFeedback(response.feedback);
        if (response.pagination) {
          setPagination(response.pagination);
        }
      } else {
        const errorMsg = response.error || t("reports.loadFailed");
        setError(errorMsg);
        toast.error(errorMsg);
      }
    } catch (err) {
      console.error("Error fetching feedback:", err);
      const errorMsg = t("reports.loadFailed");
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await getAllFeedbackStats(selectedHelpCenterId || undefined);
      if (response.ok && response.stats) {
        setStats(response.stats);
      }
    } catch (err) {
      console.error("Error fetching stats:", err);
    }
  };

  const handleRowClick = (feedbackItem: any) => {
    setSelectedFeedback(feedbackItem);
    setShowFeedbackDrawer(true);
  };

  const handleOpenItem = (e: React.MouseEvent, feedbackItem: any) => {
    e.stopPropagation();
    const url = getItemUrl(feedbackItem);
    if (url) {
      openExternalUrl(url);
    }
  };

  const handleOpenItemFromDrawer = (url: string) => {
    openExternalUrl(url);
  };

  // Update URL params when filters change
  const updateUrlParams = (newFilters: FilterState, helpCenterId?: string) => {
    const params = new URLSearchParams();
    
    if (helpCenterId) {
      params.set("helpCenterId", helpCenterId);
    } else if (selectedHelpCenterId) {
      params.set("helpCenterId", selectedHelpCenterId);
    }

    if (newFilters.feedbackType) {
      params.set("type", newFilters.feedbackType);
    }
    if (newFilters.rating !== undefined) {
      params.set("rating", newFilters.rating === null ? "null" : newFilters.rating.toString());
    }
    if (newFilters.userHash) {
      params.set("userHash", newFilters.userHash);
    }
    if (newFilters.startDate) {
      params.set("startDate", newFilters.startDate);
    }
    if (newFilters.endDate) {
      params.set("endDate", newFilters.endDate);
    }
    if (newFilters.page > 1) {
      params.set("page", newFilters.page.toString());
    }

    setSearchParams(params, { replace: true });
  };

  const clearFilters = () => {
    const clearedFilters: FilterState = {
      feedbackType: "",
      rating: undefined,
      userHash: "",
      startDate: "",
      endDate: "",
      page: 1,
      limit: 50,
    };
    setFilters(clearedFilters);
    updateUrlParams(clearedFilters, selectedHelpCenterId || undefined);
  };

  const handleFiltersChange = (newFilters: Partial<FilterState>) => {
    const updatedFilters = { ...filters, ...newFilters };
    setFilters(updatedFilters);
    updateUrlParams(updatedFilters);
  };

  const handleHelpCenterChange = (helpCenterId: string) => {
    updateUrlParams(filters, helpCenterId || undefined);
  };

  const handlePageChange = (page: number) => {
    const updatedFilters = { ...filters, page };
    setFilters(updatedFilters);
    updateUrlParams(updatedFilters);
  };

  const hasActiveFilters = Boolean(
    filters.feedbackType ||
    filters.rating !== undefined ||
    filters.userHash ||
    filters.startDate ||
    filters.endDate ||
    selectedHelpCenterId
  );

  const breadcrumbs = [
    { title: t("dashboard.breadcrumb"), href: "/dashboard" },
    { title: t("sidebar.reports"), isCurrentPage: true },
  ];

  if (loading && feedback.length === 0) {
    return (
      <AppLayout showSidebar={true} breadcrumbs={breadcrumbs}>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </AppLayout>
    );
  }

  if (error) {
    return (
      <AppLayout showSidebar={true} breadcrumbs={breadcrumbs}>
        <div className="p-4">
          <div className="text-destructive">
            {error || t("reports.loadFailed")}
          </div>
          <Button onClick={() => navigate("/help-centers")} className="mt-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t("common.backToHelpCenters")}
          </Button>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout showSidebar={true} breadcrumbs={breadcrumbs}>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{t("sidebar.reports")}</h1>
          </div>
        </div>

        {/* Premium Feature Banner */}
        {!isProCloudPlan && (
          <div className="mb-6">
            <PaidFeatureBanner />
          </div>
        )}

        {/* Locked Content Wrapper */}
        <div
          className={`space-y-6 ${
            !isProCloudPlan ? "opacity-50 pointer-events-none" : ""
          }`}
        >
          {/* Filters */}
          <ReportsFilters
            helpCenters={helpCenters}
            selectedHelpCenterId={selectedHelpCenterId}
            filters={filters}
            onHelpCenterChange={handleHelpCenterChange}
            onFiltersChange={handleFiltersChange}
            onClearFilters={clearFilters}
            hasActiveFilters={hasActiveFilters}
          />

          {/* Stats Cards */}
          <ReportsStatsCards stats={stats} />

          {/* Feedback Table */}
          <ReportsTable
            feedback={feedback}
            pagination={pagination}
            onRowClick={handleRowClick}
            onOpenItem={handleOpenItem}
            onPageChange={handlePageChange}
            formatDate={formatDate}
            getRatingIcon={getRatingIcon}
            getItemUrl={getItemUrl}
          />
        </div>
      </div>

      {/* Feedback Details Drawer */}
      <FeedbackDetailsDrawer
        feedback={selectedFeedback}
        isOpen={showFeedbackDrawer}
        onClose={() => setShowFeedbackDrawer(false)}
        formatDate={formatDate}
        getRatingIcon={getRatingIcon}
        getItemUrl={getItemUrl}
        onOpenItem={handleOpenItemFromDrawer}
      />
    </AppLayout>
  );
};

export default ReportsPage;
